import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  IconButton,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  CircularProgress,
  Alert,
  TextField,
  Tooltip,
  Chip,
  Tabs,
  Tab,
  Grid
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import MergeIcon from '@mui/icons-material/MergeType';
import DeleteIcon from '@mui/icons-material/Delete';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockIcon from '@mui/icons-material/Lock';
import TranslateIcon from '@mui/icons-material/Translate';
import { format, formatDistanceToNow } from 'date-fns';
import { getAuthHeaders } from '../../utils/authUtils';
import { formatTimeFromSeconds } from '../../utils/formatUtils';
import sectionManagementService from '../../services/SectionManagementService';
import VideoCRUDService from '../../services/VideoCRUDService';
import { useTrackUsage } from '../../hooks/useTrackUsage';

const SectionManagerDialog = ({ open, onClose, videoId }) => {
  useTrackUsage('SectionManagerDialog', 'dialog', 'folder-browser', open);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [sectionCount, setSectionCount] = useState(4);
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedSection, setSelectedSection] = useState(null);
  
  // States for language handling
  const [videoLanguages, setVideoLanguages] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [loadingLanguages, setLoadingLanguages] = useState(false);

  // Fetch video details to get available languages
  const fetchVideoDetails = async () => {
    if (!videoId) return;
    
    setLoadingLanguages(true);
    setError(null);
    const {email, auth} = getAuthHeaders();
    try {
      const response = await VideoCRUDService.getVideoById(videoId, email, auth);
      
      // Extract languages from the video response
      const languages = response.data.videoLanguages || [];
      setVideoLanguages(languages);
      
      // Set first language as selected if available
      if (languages.length > 0 && !selectedLanguage) {
        setSelectedLanguage(languages[0].languageName);
      }
    } catch (err) {
      console.error("Error fetching video details:", err);
      setError("Failed to fetch video languages. Please try again.");
    } finally {
      setLoadingLanguages(false);
    }
  };

  const fetchSections = async () => {
    if (!videoId || !selectedLanguage) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await sectionManagementService.getSections(videoId, selectedLanguage);
      setSections(response.data || []);
    } catch (err) {
      console.error("Error fetching sections:", err);
      setError("Failed to fetch sections. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch video details when dialog opens
  useEffect(() => {
    if (open && videoId) {
      fetchVideoDetails();
    }
  }, [open, videoId]);
  
  // Fetch sections when language changes
  useEffect(() => {
    if (open && videoId && selectedLanguage) {
      fetchSections();
    }
  }, [open, videoId, selectedLanguage]);
  
  const handleCreateSections = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await sectionManagementService.createSections(videoId, selectedLanguage, sectionCount);
      setSections(response.data || []);
      setCreateDialogOpen(false);
    } catch (err) {
      console.error("Error creating sections:", err);
      setError("Failed to create sections: " + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };
  
  const handleMergeSections = async () => {
    setLoading(true);
    setError(null);
    
    try {
      await sectionManagementService.mergeSections(videoId, selectedLanguage);
      setMergeDialogOpen(false);
      fetchSections();
    } catch (err) {
      console.error("Error merging sections:", err);
      setError("Failed to merge sections: " + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeleteSection = async () => {
    if (!selectedSection) return;
    
    setLoading(true);
    setError(null);
    
    try {
      await sectionManagementService.deleteSection(videoId, selectedLanguage, selectedSection.id);
      setDeleteDialogOpen(false);
      setSelectedSection(null);
      fetchSections();
    } catch (err) {
      console.error("Error deleting section:", err);
      setError("Failed to delete section: " + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };
  
  const handleEditSection = async (section) => {
    try {
      setLoading(true);
      
      // First, start the section editing session via the API
      const response = await sectionManagementService.startSectionEditing(
        videoId, 
        selectedLanguage, 
        section.id
      );
      
      // If successful, open the editor in a new window/tab with all necessary parameters
      if (response && response.data) {
        // Create URL with all required parameters - use proper query parameter format
        const url = `/videoplayer/${videoId}?sectionId=${section.id}&languageName=${encodeURIComponent(selectedLanguage)}`;
        
        console.log("Opening editor with URL:", url); // Debug log
        
        // Store section data in localStorage so it can be retrieved by the editor
        const sectionData = {
          videoId,
          languageName: selectedLanguage,
          sectionId: section.id,
          sectionName: section.name,
          startTime: section.startTime,
          endTime: section.endTime,
          vttFileUrl: response.data.vttFileUrl || section.vttFileUrl
        };
        
        localStorage.setItem(`section_edit_${section.id}`, JSON.stringify(sectionData));
        console.log("Stored section data in localStorage:", sectionData); // Debug log
        
        window.open(url, '_blank');
      }
    } catch (err) {
      console.error("Error starting section editing:", err);
      setError("Failed to start editing session: " + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newTabIndex) => {
    if (videoLanguages[newTabIndex]) {
      setSelectedTabIndex(newTabIndex);
      setSelectedLanguage(videoLanguages[newTabIndex].languageName);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          height: '80vh',
          maxHeight: '800px',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Subtitle Sections</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      {/* Language Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
        {loadingLanguages ? (
          <Box sx={{ display: 'flex', alignItems: 'center', py: 1 }}>
            <CircularProgress size={24} sx={{ mr: 2 }} />
            <Typography>Loading languages...</Typography>
          </Box>
        ) : videoLanguages.length === 0 ? (
          <Alert severity="warning" sx={{ my: 1 }}>
            No languages available for this video
          </Alert>
        ) : (
          <Tabs 
            value={selectedTabIndex} 
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            aria-label="language tabs"
          >
            {videoLanguages.map((lang, index) => (
              <Tab 
                key={lang.languageName} 
                label={lang.languageName} 
                icon={<TranslateIcon fontSize="small" />} 
                iconPosition="start"
              />
            ))}
          </Tabs>
        )}
      </Box>
      
      <DialogContent sx={{ 
        overflowY: 'auto', 
        flexGrow: 1,
        p: 2,
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: '#f1f1f1',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#888',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          backgroundColor: '#555',
        },
      }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {selectedLanguage && (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="body1">
                Manage subtitle sections for <strong>{selectedLanguage}</strong> language.
              </Typography>
              
              <Box>
                <Button 
                  variant="contained" 
                  color="primary" 
                  startIcon={<AddIcon />} 
                  onClick={() => setCreateDialogOpen(true)}
                  disabled={loading || sections.length > 0}
                  sx={{ mr: 1 }}
                >
                  Create Sections
                </Button>
                
                <Button 
                  variant="outlined" 
                  startIcon={<MergeIcon />} 
                  onClick={() => setMergeDialogOpen(true)}
                  disabled={loading || sections.length === 0 || sections.some(s => s.locked)}
                >
                  Merge All
                </Button>
              </Box>
            </Box>
            
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress />
              </Box>
            ) : sections.length === 0 ? (
              <Paper sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="textSecondary">
                  No sections have been created yet for {selectedLanguage}. Click "Create Sections" to divide this subtitle into manageable parts.
                </Typography>
              </Paper>
            ) : (
              <List component={Paper}>
                {sections.map((section) => (
                  <React.Fragment key={section.id}>
                    <ListItem>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="subtitle1">
                              {section.name}
                            </Typography>
                            {section.merged && (
                              <Tooltip title="This section has been merged into the main file">
                                <CheckCircleIcon color="success" fontSize="small" sx={{ ml: 1 }} />
                              </Tooltip>
                            )}
                            {section.locked && (
                              <Tooltip title={`Currently being edited by ${section.activeEditor || 'someone'}`}>
                                <LockIcon color="warning" fontSize="small" sx={{ ml: 1 }} />
                              </Tooltip>
                            )}
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2">
                              Time Range: {formatTimeFromSeconds(section.startTime)} - {formatTimeFromSeconds(section.endTime)}
                            </Typography>
                            {section.lastMergedAt && (
                              <Typography variant="caption" color="textSecondary">
                                Last merged: {formatDistanceToNow(new Date(section.lastMergedAt), { addSuffix: true })}
                              </Typography>
                            )}
                            {section.activeEditor && (
                              <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                                <AccessTimeIcon fontSize="small" color="warning" sx={{ mr: 0.5 }} />
                                <Typography variant="caption" color="warning.main">
                                  Currently being edited by {section.activeEditor} ({formatDistanceToNow(new Date(section.editStartedAt), { addSuffix: true })})
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction>
                        <Box sx={{ display: 'flex' }}>
                          <Tooltip title="Edit this section">
                            <IconButton 
                              edge="end" 
                              onClick={() => handleEditSection(section)}
                              disabled={section.activeEditor && section.activeEditor !== JSON.parse(localStorage.getItem('authUser'))?.email}
                              sx={{ mr: 1 }}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          
                          <Tooltip title="Delete this section">
                            <IconButton 
                              edge="end" 
                              color="error"
                              onClick={() => {
                                setSelectedSection(section);
                                setDeleteDialogOpen(true);
                              }}
                              disabled={section.activeEditor || section.locked}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </ListItemSecondaryAction>
                    </ListItem>
                    <Divider />
                  </React.Fragment>
                ))}
              </List>
            )}
            
            <Box sx={{ mt: 3 }}>
              <Alert severity="info">
                <Typography variant="subtitle2">About Subtitle Sections</Typography>
                <Typography variant="body2">
                  Sections allow multiple people to work on different parts of a subtitle file simultaneously. 
                  When you create sections, the subtitle is divided into equal parts. Each section can be edited independently,
                  then merged back into the full subtitle file.
                </Typography>
              </Alert>
            </Box>
          </>
        )}
      </DialogContent>
      
      {/* Create Sections Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)}>
        <DialogTitle>Create Subtitle Sections</DialogTitle>
        <DialogContent>
          <Typography paragraph>
            Divide this subtitle into multiple sections that can be edited independently. This is useful for large videos where multiple people need to work on different parts.
          </Typography>
          
          <TextField
            label="Number of Sections"
            type="number"
            fullWidth
            margin="normal"
            value={sectionCount}
            onChange={(e) => setSectionCount(Math.max(2, Math.min(20, parseInt(e.target.value) || 2)))}
            inputProps={{ min: 2, max: 20 }}
            helperText="Minimum: 2, Maximum: 20"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleCreateSections} 
            color="primary" 
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Merge Sections Dialog */}
      <Dialog open={mergeDialogOpen} onClose={() => setMergeDialogOpen(false)}>
        <DialogTitle>Merge All Sections</DialogTitle>
        <DialogContent>
          <Typography paragraph>
            This will combine all sections back into a single subtitle file. Any edits made to the sections will be included.
          </Typography>
          
          <Alert severity="warning">
            All sections must be closed (not being actively edited) before merging. Make sure all editors have saved their work.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMergeDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleMergeSections} 
            color="primary" 
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Merge'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Section Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Section</DialogTitle>
        <DialogContent>
          <Typography paragraph>
            Are you sure you want to delete the section "{selectedSection?.name}"?
          </Typography>
          <Alert severity="error">
            This action cannot be undone. Any edits made to this section will be lost.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleDeleteSection} 
            color="error" 
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default SectionManagerDialog;